import React, { useState, useEffect } from 'react';
import { 
  FileText, Send, Eye, RotateCcw, XCircle, Clock, CheckCircle, 
  AlertTriangle, Users, Search, Copy, Plus,
  Calendar, MapPin, Loader, X, Lock, Sparkles, Code
} from 'lucide-react';
import { useBusinessContext } from '../contexts/BusinessContext';
import api from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import TemplateCard from '../components/agreements/TemplateCard';
import EditTemplateModal from '../components/agreements/EditTemplateModal';

const ServiceAgreementsPage = () => {
  const { subscriptionPlan, isTrialActive } = useBusinessContext();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('agreements');
  
  // Agreements list state
  const [agreements, setAgreements] = useState([]);
  const [summary, setSummary] = useState({ total: 0, pending: 0, signed: 0, expired: 0, voided: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [canEditTemplates, setCanEditTemplates] = useState(false);
  const [canCreateCustom, setCanCreateCustom] = useState(false);
  
  // Modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  
  // Send modal state
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [sendForm, setSendForm] = useState({ customerId: '', templateId: '', jobId: '' });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // Edit template state
  const [editForm, setEditForm] = useState({
    name: '',
    title: '',
    htmlContent: '',
    description: '',
    includePhotoConsent: false,
    includeCancellationPolicy: true,
    includeWeatherPolicy: false,
    signatureLinkExpirationDays: 7
  });
  const [saving, setSaving] = useState(false);

  // Toast state
  const [toast, setToast] = useState(null);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger',
    confirmText: 'Confirm',
    showInput: false,
    inputLabel: '',
    inputValue: ''
  });

  useEffect(() => {
    if (activeTab === 'agreements') {
      fetchAgreements();
    } else {
      fetchTemplates();
    }
  }, [activeTab, statusFilter]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ==================== AGREEMENTS FUNCTIONS ====================

  const fetchAgreements = async () => {
    try {
      setLoading(true);
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await api.get(`/serviceagreements${params}`);
      setAgreements(response.data.agreements || []);
      setSummary(response.data.summary || { total: 0, pending: 0, signed: 0, expired: 0, voided: 0 });
    } catch (err) {
      console.error('Error fetching agreements:', err);
      setError('Failed to load agreements');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchCustomerJobs = async (customerId) => {
    if (!customerId) {
      setJobs([]);
      return;
    }
    try {
      const response = await api.get(`/jobs/customer/${customerId}`);
      setJobs(response.data || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setJobs([]);
    }
  };

  const openSendModal = async () => {
    setShowSendModal(true);
    setSendForm({ customerId: '', templateId: '', jobId: '' });
    setSendResult(null);
    setJobs([]);
    await Promise.all([fetchTemplates(), fetchCustomers()]);
  };

  const handleCustomerChange = (customerId) => {
    setSendForm({ ...sendForm, customerId, jobId: '' });
    fetchCustomerJobs(customerId);
  };

  const handleSendAgreement = async () => {
    if (!sendForm.customerId || !sendForm.templateId) {
      showToast('Please select a customer and template', 'error');
      return;
    }

    try {
      setSending(true);
      const response = await api.post('/serviceagreements/send', {
        customerId: parseInt(sendForm.customerId),
        templateId: parseInt(sendForm.templateId),
        jobId: sendForm.jobId ? parseInt(sendForm.jobId) : null
      });
      
      setSendResult({
        success: true,
        signingUrl: response.data.signingUrl,
        expiresAt: response.data.expiresAt
      });
      
      fetchAgreements();
    } catch (err) {
      console.error('Error sending agreement:', err);
      setSendResult({
        success: false,
        error: err.response?.data?.error || 'Failed to send agreement'
      });
    } finally {
      setSending(false);
    }
  };

  const handleViewDetails = async (agreement) => {
    try {
      const response = await api.get(`/serviceagreements/${agreement.id}`);
      setSelectedAgreement(response.data);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Error fetching agreement details:', err);
      showToast('Failed to load agreement details', 'error');
    }
  };

  const handleResend = (agreementId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Resend Agreement',
      message: 'This will generate a new signing link and send a reminder email. The previous link will no longer work.',
      variant: 'warning',
      confirmText: 'Resend',
      showInput: false,
      inputLabel: '',
      inputValue: '',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          const response = await api.post(`/serviceagreements/${agreementId}/resend`);
          showToast(`Agreement resent! Expires: ${new Date(response.data.newExpiresAt).toLocaleDateString()}`);
          fetchAgreements();
        } catch (err) {
          console.error('Error resending agreement:', err);
          showToast(err.response?.data?.error || 'Failed to resend agreement', 'error');
        }
      }
    });
  };

  const handleVoid = (agreementId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Void Agreement',
      message: 'This will permanently void this agreement. The customer will no longer be able to sign it.',
      variant: 'danger',
      confirmText: 'Void Agreement',
      showInput: true,
      inputLabel: 'Reason for voiding (optional)',
      inputValue: '',
      onConfirm: async () => {
        const reason = confirmModal.inputValue;
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          await api.post(`/serviceagreements/${agreementId}/void`, { reason });
          showToast('Agreement voided successfully');
          fetchAgreements();
          setShowDetailModal(false);
        } catch (err) {
          console.error('Error voiding agreement:', err);
          showToast(err.response?.data?.error || 'Failed to void agreement', 'error');
        }
      }
    });
  };

  // ==================== TEMPLATES FUNCTIONS ====================

  const fetchTemplates = async () => {
    try {
      setTemplatesLoading(true);
      const response = await api.get('/serviceagreements/templates');
      setTemplates(response.data.templates || []);
      setCanEditTemplates(response.data.canEditTemplates || false);
      setCanCreateCustom(response.data.canCreateCustom || false);
    } catch (err) {
      console.error('Error fetching templates:', err);
      showToast('Failed to load templates', 'error');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleCopyTemplate = async (templateId) => {
    try {
      const response = await api.post(`/serviceagreements/templates/${templateId}/copy`);
      showToast(response.data.message);
      fetchTemplates();
      
      setSelectedTemplate(response.data.template);
      setEditForm({
        name: response.data.template.name,
        title: response.data.template.title,
        htmlContent: response.data.template.htmlContent,
        description: response.data.template.description || '',
        includePhotoConsent: response.data.template.includePhotoConsent,
        includeCancellationPolicy: response.data.template.includeCancellationPolicy,
        includeWeatherPolicy: response.data.template.includeWeatherPolicy,
        signatureLinkExpirationDays: 7
      });
      setShowEditTemplateModal(true);
    } catch (err) {
      console.error('Error copying template:', err);
      if (err.response?.data?.existingId) {
        showToast('You already have a custom version of this template', 'error');
      } else {
        showToast(err.response?.data?.error || 'Failed to copy template', 'error');
      }
    }
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setEditForm({
      name: template.name,
      title: template.title,
      htmlContent: template.htmlContent,
      description: template.description || '',
      includePhotoConsent: template.includePhotoConsent,
      includeCancellationPolicy: template.includeCancellationPolicy,
      includeWeatherPolicy: template.includeWeatherPolicy,
      signatureLinkExpirationDays: 7
    });
    setShowEditTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      setSaving(true);
      await api.put(`/serviceagreements/templates/${selectedTemplate.id}`, editForm);
      showToast('Template saved successfully');
      setShowEditTemplateModal(false);
      fetchTemplates();
    } catch (err) {
      console.error('Error saving template:', err);
      showToast(err.response?.data?.error || 'Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      setSaving(true);
      await api.post('/serviceagreements/templates', editForm);
      showToast('Template created successfully');
      setShowEditTemplateModal(false);
      fetchTemplates();
    } catch (err) {
      console.error('Error creating template:', err);
      showToast(err.response?.data?.error || 'Failed to create template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = (templateId, templateName) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Template',
      message: `Are you sure you want to delete "${templateName}"?`,
      variant: 'danger',
      confirmText: 'Delete',
      showInput: false,
      inputLabel: '',
      inputValue: '',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          const response = await api.delete(`/serviceagreements/templates/${templateId}`);
          showToast(response.data.message);
          fetchTemplates();
        } catch (err) {
          console.error('Error deleting template:', err);
          showToast(err.response?.data?.error || 'Failed to delete template', 'error');
        }
      }
    });
  };

  const handlePreviewTemplate = async (templateId) => {
    try {
      const response = await api.post(`/serviceagreements/templates/${templateId}/preview`);
      setPreviewContent(response.data);
      setShowPreviewModal(true);
    } catch (err) {
      console.error('Error previewing template:', err);
      showToast('Failed to load preview', 'error');
    }
  };

  const openCreateModal = () => {
    setSelectedTemplate(null);
    setEditForm({
      name: '',
      title: 'Service Agreement',
      htmlContent: '<h2>Service Agreement</h2>\n<p>This agreement is between {{BusinessName}} and {{CustomerName}}...</p>',
      description: '',
      includePhotoConsent: false,
      includeCancellationPolicy: true,
      includeWeatherPolicy: false,
      signatureLinkExpirationDays: 7
    });
    setShowEditTemplateModal(true);
  };

  // ==================== HELPERS ====================

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard!');
  };

  const getStatusBadge = (status) => {
    const styles = {
      Pending: 'bg-yellow-100 text-yellow-800',
      Signed: 'bg-green-100 text-green-800',
      Expired: 'bg-gray-100 text-gray-600',
      Voided: 'bg-red-100 text-red-800'
    };
    const icons = {
      Pending: <Clock className="h-3 w-3" />,
      Signed: <CheckCircle className="h-3 w-3" />,
      Expired: <AlertTriangle className="h-3 w-3" />,
      Voided: <XCircle className="h-3 w-3" />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {icons[status]}
        {status}
      </span>
    );
  };

  const filteredAgreements = agreements.filter(a => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      a.customerName?.toLowerCase().includes(search) ||
      a.customerEmail?.toLowerCase().includes(search) ||
      a.agreementTitle?.toLowerCase().includes(search)
    );
  });

  const systemTemplates = templates.filter(t => !t.isCustom);
  const customTemplates = templates.filter(t => t.isCustom);

  // Status filter data
  const statusFilters = [
    { value: '', label: 'Total', count: summary.total, color: 'gray' },
    { value: 'Pending', label: 'Pending', count: summary.pending, color: 'yellow' },
    { value: 'Signed', label: 'Signed', count: summary.signed, color: 'green' },
    { value: 'Expired', label: 'Expired', count: summary.expired, color: 'gray' },
    { value: 'Voided', label: 'Voided', count: summary.voided, color: 'red' },
  ];
  // ==================== RENDER ====================

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto pb-20 lg:pb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Service Agreements</h1>
          <p className="text-sm text-gray-600 mt-1">Send and manage customer agreements</p>
        </div>
        <button
          onClick={openSendModal}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium w-full sm:w-auto"
        >
          <Send className="h-4 w-4" />
          Send Agreement
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-4 sm:mb-6 -mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto">
        <button
          onClick={() => setActiveTab('agreements')}
          className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'agreements'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <FileText className="h-4 w-4" />
          <span>Agreements</span>
          {summary.total > 0 && (
            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
              {summary.total}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'templates'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          <Code className="h-4 w-4" />
          <span>Templates</span>
        </button>
      </div>

      {/* ==================== AGREEMENTS TAB ==================== */}
      {activeTab === 'agreements' && (
        <>
          {/* Summary Cards - Horizontal scroll on mobile */}
          <div className="flex gap-2 mb-4 -mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto pb-2 sm:pb-0 sm:grid sm:grid-cols-5 sm:gap-3">
            {statusFilters.map((item) => (
              <button
                key={item.value}
                onClick={() => setStatusFilter(item.value)}
                className={`flex-shrink-0 min-w-[72px] p-3 sm:p-4 rounded-xl border-2 transition-all text-center ${
                  statusFilter === item.value 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <p className={`text-lg sm:text-2xl font-bold ${
                  item.color === 'yellow' ? 'text-yellow-600' :
                  item.color === 'green' ? 'text-green-600' :
                  item.color === 'red' ? 'text-red-600' :
                  'text-gray-900'
                }`}>
                  {item.count}
                </p>
                <p className="text-xs text-gray-600">{item.label}</p>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search customer or title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Agreements List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <Loader className="h-8 w-8 text-blue-600 animate-spin mx-auto mb-2" />
                <p className="text-gray-600 text-sm">Loading agreements...</p>
              </div>
            ) : error ? (
              <div className="p-8 text-center">
                <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-red-600 text-sm">{error}</p>
                <button onClick={fetchAgreements} className="mt-2 text-blue-600 hover:underline text-sm">
                  Try again
                </button>
              </div>
            ) : filteredAgreements.length === 0 ? (
              <div className="p-8 text-center">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-base font-medium text-gray-900 mb-1">No agreements found</h3>
                <p className="text-gray-600 text-sm mb-4">
                  {statusFilter ? `No ${statusFilter.toLowerCase()} agreements` : 'Send your first agreement'}
                </p>
                <button
                  onClick={openSendModal}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  <Send className="h-4 w-4" />
                  Send Agreement
                </button>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Agreement</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Signed</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredAgreements.map((agreement) => (
                        <tr key={agreement.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900 text-sm">{agreement.customerName}</p>
                            <p className="text-xs text-gray-500 truncate max-w-[200px]">{agreement.customerEmail}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-gray-900 text-sm truncate max-w-[200px]">{agreement.agreementTitle}</p>
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(agreement.status)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {agreement.sentAt ? new Date(agreement.sentAt).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {agreement.signedAt ? new Date(agreement.signedAt).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleViewDetails(agreement)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                                title="View"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              {agreement.status === 'Pending' && (
                                <button
                                  onClick={() => handleResend(agreement.id)}
                                  className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                                  title="Resend"
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </button>
                              )}
                              {(agreement.status === 'Pending' || agreement.status === 'Signed') && (
                                <button
                                  onClick={() => handleVoid(agreement.id)}
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                  title="Void"
                                >
                                  <XCircle className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card Layout */}
                <div className="lg:hidden divide-y divide-gray-200">
                  {filteredAgreements.map((agreement) => (
                    <div key={agreement.id} className="p-4">
                      <div className="flex justify-between items-start gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{agreement.customerName}</p>
                          <p className="text-xs text-gray-500 truncate">{agreement.customerEmail}</p>
                        </div>
                        {getStatusBadge(agreement.status)}
                      </div>
                      
                      <p className="text-sm text-gray-700 mb-2 truncate">{agreement.agreementTitle}</p>
                      
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-500 space-x-2">
                          {agreement.sentAt && (
                            <span>Sent {new Date(agreement.sentAt).toLocaleDateString()}</span>
                          )}
                          {agreement.signedAt && (
                            <span className="text-green-600">
                              • Signed {new Date(agreement.signedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center">
                          <button
                            onClick={() => handleViewDetails(agreement)}
                            className="p-2 text-gray-400 hover:text-blue-600"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {agreement.status === 'Pending' && (
                            <button
                              onClick={() => handleResend(agreement.id)}
                              className="p-2 text-gray-400 hover:text-green-600"
                            >
                              <RotateCcw className="h-4 w-4" />
                            </button>
                          )}
                          {(agreement.status === 'Pending' || agreement.status === 'Signed') && (
                            <button
                              onClick={() => handleVoid(agreement.id)}
                              className="p-2 text-gray-400 hover:text-red-600"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* ==================== TEMPLATES TAB ==================== */}
      {activeTab === 'templates' && (
        <div className="space-y-6">
          {/* Tier Info Banner */}
          {!canEditTemplates && (
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Lock className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-purple-900 text-sm">Upgrade to Customize</h3>
                  <p className="text-xs text-purple-700 mt-1">
                    Pro and Enterprise plans can customize agreement templates.
                  </p>
                  <a href="/settings" className="text-xs font-medium text-purple-600 hover:text-purple-700 mt-1 inline-block">
                    View Plans →
                  </a>
                </div>
              </div>
            </div>
          )}

          {templatesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
          ) : (
            <>
              {/* My Templates Section */}
              {customTemplates.length > 0 && (
                <div>
                  <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-600" />
                    My Templates
                  </h2>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {customTemplates.map((template) => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        isCustom={true}
                        canEdit={canEditTemplates}
                        onPreview={handlePreviewTemplate}
                        onEdit={handleEditTemplate}
                        onDelete={handleDeleteTemplate}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* System Templates Section */}
              <div>
                <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  {customTemplates.length > 0 ? 'Available Templates' : 'Templates'}
                </h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  {systemTemplates.map((template) => (
                    <TemplateCard
                      key={template.id}
                      template={template}
                      isCustom={false}
                      canEdit={canEditTemplates}
                      onPreview={handlePreviewTemplate}
                      onCopy={handleCopyTemplate}
                    />
                  ))}
                </div>
              </div>

              {/* Create Custom Template - Enterprise Only */}
              {canCreateCustom && (
                <button
                  onClick={openCreateModal}
                  className="w-full border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-purple-400 transition-colors group"
                >
                  <Plus className="h-8 w-8 text-gray-400 mx-auto mb-2 group-hover:text-purple-500 transition-colors" />
                  <h3 className="font-medium text-gray-900 text-sm">Create Custom Template</h3>
                  <p className="text-xs text-gray-500 mt-1">Build from scratch</p>
                </button>
              )}
            </>
          )}
        </div>
      )}
      {/* ==================== SEND AGREEMENT MODAL ==================== */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[95vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Send Agreement</h2>
              <button
                onClick={() => setShowSendModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg -mr-2"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4">
              {sendResult?.success ? (
                <div className="text-center py-4">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-7 w-7 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Agreement Sent!</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    The customer will receive an email with the signing link.
                  </p>
                  
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <p className="text-xs text-gray-600 mb-2">Signing Link:</p>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={sendResult.signingUrl}
                        readOnly
                        className="flex-1 px-2 py-1.5 bg-white border border-gray-300 rounded text-xs truncate"
                      />
                      <button
                        onClick={() => copyToClipboard(sendResult.signingUrl)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded flex-shrink-0"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Expires: {new Date(sendResult.expiresAt).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setSendResult(null);
                        setSendForm({ customerId: '', templateId: '', jobId: '' });
                        setJobs([]);
                      }}
                      className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                    >
                      Send Another
                    </button>
                    <button
                      onClick={() => setShowSendModal(false)}
                      className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {sendResult?.error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {sendResult.error}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                      <select
                        value={sendForm.customerId}
                        onChange={(e) => handleCustomerChange(e.target.value)}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">Select a customer...</option>
                        {customers.map((customer) => (
                          <option key={customer.id} value={customer.id}>
                            {customer.name} {customer.email ? `(${customer.email})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Template *</label>
                      <select
                        value={sendForm.templateId}
                        onChange={(e) => setSendForm({ ...sendForm, templateId: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">Select a template...</option>
                        {templates.filter(t => t.isAccessible).map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.name}{template.isCustom ? ' (Custom)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    {sendForm.customerId && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Link to Job (Optional)</label>
                        <select
                          value={sendForm.jobId}
                          onChange={(e) => setSendForm({ ...sendForm, jobId: e.target.value })}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                          <option value="">No job linked</option>
                          {jobs.filter(job => ['Quoted', 'Scheduled'].includes(job.status)).map((job) => (
                            <option key={job.id} value={job.id}>
                              {job.address}{job.scheduledDate ? ` - ${new Date(job.scheduledDate).toLocaleDateString()}` : ''} - {job.status}{job.quoteAmount ? ` - $${Number(job.quoteAmount).toLocaleString()}` : ''}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Auto-fills job details in the agreement</p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={() => setShowSendModal(false)}
                      className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendAgreement}
                      disabled={sending || !sendForm.customerId || !sendForm.templateId}
                      className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      {sending ? (
                        <>
                          <Loader className="h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          Send
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== AGREEMENT DETAIL MODAL ==================== */}
      {showDetailModal && selectedAgreement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[95vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-bold text-gray-900 truncate">{selectedAgreement.agreementTitle}</h2>
                <div className="mt-1">{getStatusBadge(selectedAgreement.status)}</div>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Customer Info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <h3 className="font-medium text-gray-900 mb-2 text-sm flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Customer
                </h3>
                <p className="text-sm font-medium">{selectedAgreement.customer?.name}</p>
                <p className="text-xs text-gray-500">{selectedAgreement.customer?.email || '-'}</p>
              </div>

              {/* Linked Job */}
              {selectedAgreement.job && (
                <div className="bg-blue-50 rounded-lg p-3">
                  <h3 className="font-medium text-gray-900 mb-2 text-sm flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Linked Job
                  </h3>
                  <p className="text-sm">{selectedAgreement.job.description}</p>
                </div>
              )}

              {/* Signature Details */}
              {selectedAgreement.status === 'Signed' && selectedAgreement.signature && (
                <div className="bg-green-50 rounded-lg p-3">
                  <h3 className="font-medium text-gray-900 mb-2 text-sm flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Signature
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-gray-500">Signed By</p>
                      <p className="font-medium">{selectedAgreement.signature.signerName}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Date</p>
                      <p className="font-medium">{new Date(selectedAgreement.signature.signedAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  {selectedAgreement.signature.signatureImage && (
                    <div className="mt-3 bg-white border border-gray-200 rounded p-2 inline-block">
                      <img src={selectedAgreement.signature.signatureImage} alt="Signature" className="max-h-16" />
                    </div>
                  )}
                </div>
              )}

              {/* Timeline */}
              <div className="border border-gray-200 rounded-lg p-3">
                <h3 className="font-medium text-gray-900 mb-2 text-sm flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Timeline
                </h3>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Created</span>
                    <span>{new Date(selectedAgreement.createdAt).toLocaleDateString()}</span>
                  </div>
                  {selectedAgreement.sentAt && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Sent</span>
                      <span>{new Date(selectedAgreement.sentAt).toLocaleDateString()}</span>
                    </div>
                  )}
                  {selectedAgreement.viewCount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Views</span>
                      <span>{selectedAgreement.viewCount}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                {selectedAgreement.status === 'Pending' && (
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      handleResend(selectedAgreement.id);
                    }}
                    className="flex-1 px-3 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2 text-sm font-medium"
                  >
                    <RotateCcw className="h-4 w-4" />
                    Resend
                  </button>
                )}
                {(selectedAgreement.status === 'Pending' || selectedAgreement.status === 'Signed') && (
                  <button
                    onClick={() => handleVoid(selectedAgreement.id)}
                    className="flex-1 px-3 py-2.5 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 flex items-center justify-center gap-2 text-sm font-medium"
                  >
                    <XCircle className="h-4 w-4" />
                    Void
                  </button>
                )}
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="flex-1 px-3 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== PREVIEW MODAL ==================== */}
      {showPreviewModal && previewContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[95vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Template Preview</h2>
                <p className="text-xs text-gray-500 mt-0.5">Using sample data</p>
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <h3 className="text-base font-semibold text-gray-900 mb-3 pb-3 border-b border-gray-200">
                  {previewContent.title}
                </h3>
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: previewContent.htmlContent }}
                />
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="w-full px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== EDIT TEMPLATE MODAL ==================== */}
      <EditTemplateModal
        isOpen={showEditTemplateModal}
        onClose={() => setShowEditTemplateModal(false)}
        isEditing={!!selectedTemplate}
        editForm={editForm}
        setEditForm={setEditForm}
        onSave={selectedTemplate ? handleSaveTemplate : handleCreateTemplate}
        saving={saving}
      />

      {/* ==================== CONFIRM MODAL ==================== */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText={confirmModal.confirmText}
        showInput={confirmModal.showInput}
        inputLabel={confirmModal.inputLabel}
        inputValue={confirmModal.inputValue}
        onInputChange={(value) => setConfirmModal(prev => ({ ...prev, inputValue: value }))}
      />

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed bottom-20 sm:bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
        }`}>
          {toast.type === 'error' ? (
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          ) : (
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
          )}
          <span className="text-sm flex-1">{toast.message}</span>
        </div>
      )}
    </div>
  );
};

export default ServiceAgreementsPage;